/**
 * Proyecto: MOGOTAX
 * Módulo TAI - Formulario de Transportadoras
 * Fecha: 18 de Febrero 2026
 */

import React, { useEffect, useState } from "react";
import {
  Modal,
  Button,
  Form,
  Input,
  Select,
  Row,
  Col,
  message,
  Spin,
  Switch,
  Divider,
  DatePicker,
  Checkbox,
} from "antd";
import PropTypes from "prop-types";
import api from "../../../axiosConfig";
import {
  CarOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  MailOutlined,
  PhoneOutlined,
  HomeOutlined,
  UserOutlined,
  NumberOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

const { Option } = Select;
const { TextArea } = Input;

const TIPOS_TRANSPORTE = [
  { value: "bus", label: "Bus" },
  { value: "taxi", label: "Taxi" },
  { value: "especial", label: "Especial" },
  { value: "mixto", label: "Mixto" },
];

const TransportadoraForm = ({
  open,
  handleClose,
  fetchTransportadoras,
  initialData = null,
  zonas = [],
  isEdit = false,
}) => {
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contratoIndefinido, setContratoIndefinido] = useState(false);

  useEffect(() => {
    if (initialData) {
      const formData = {
        ...initialData,
        zona_id: initialData.zona_id || undefined,
        contrato_fecha_inicio: initialData.contrato_fecha_inicio
          ? dayjs(initialData.contrato_fecha_inicio)
          : null,
        contrato_fecha_fin: initialData.contrato_fecha_fin
          ? dayjs(initialData.contrato_fecha_fin)
          : null,
        contrato_indefinido: initialData.contrato_indefinido || false,
      };
      form.setFieldsValue(formData);
      setContratoIndefinido(initialData.contrato_indefinido || false);
    } else {
      form.resetFields();
      form.setFieldsValue({
        tipo_transporte: "mixto",
        estado: "activo",
        es_mogotax_especial: false,
        contrato_indefinido: false,
      });
      setContratoIndefinido(false);
    }
  }, [initialData, form]);

  const handleContratoIndefinidoChange = (e) => {
    const checked = e.target.checked;
    setContratoIndefinido(checked);
    if (checked) {
      form.setFieldValue('contrato_fecha_fin', null);
    }
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const values = await form.validateFields();

      // Formatear fechas antes de enviar
      const dataToSend = {
        ...values,
        contrato_fecha_inicio: values.contrato_fecha_inicio
          ? values.contrato_fecha_inicio.format('YYYY-MM-DD')
          : null,
        contrato_fecha_fin: contratoIndefinido
          ? null
          : (values.contrato_fecha_fin ? values.contrato_fecha_fin.format('YYYY-MM-DD') : null),
        contrato_indefinido: contratoIndefinido,
      };

      if (isEdit && initialData?.id) {
        await api.put(`/transportadoras/${initialData.id}`, dataToSend);
        message.success("Transportadora actualizada exitosamente");
      } else {
        await api.post("/transportadoras", dataToSend);
        message.success("Transportadora creada exitosamente");
      }

      fetchTransportadoras();
      handleClose();
    } catch (error) {
      console.error("Error al guardar transportadora:", error);
      if (error.response?.data?.error) {
        message.error(error.response.data.error);
      } else {
        message.error("Error al guardar la transportadora");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title={
        <div
          style={{
            background: "linear-gradient(90deg, #0a2e5c, #004d99)",
            padding: "12px",
            borderRadius: "8px 8px 0 0",
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
            color: "white",
            fontSize: "20px",
            display: "flex",
            alignItems: "center",
          }}
        >
          {isEdit ? (
            <>
              <EditOutlined style={{ marginRight: "8px" }} />
              Editar Transportadora
            </>
          ) : (
            <>
              <CarOutlined style={{ marginRight: "8px" }} />
              Agregar Transportadora
            </>
          )}
        </div>
      }
      footer={null}
      width={900}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Spin spinning={isSubmitting} tip="Guardando...">
          <Divider orientation="left">Información General</Divider>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="nombre"
                label="Nombre"
                rules={[
                  { required: true, message: "El nombre es obligatorio" },
                ]}
              >
                <Input
                  prefix={<CarOutlined />}
                  placeholder="Nombre de la transportadora"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="nit"
                label="NIT"
                rules={[
                  {
                    pattern: /^[0-9-]+$/,
                    message: "El NIT solo puede contener números y guiones",
                  },
                ]}
              >
                <Input
                  prefix={<NumberOutlined />}
                  placeholder="NIT (ej: 900123456-7)"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="razon_social" label="Razón Social">
                <Input placeholder="Razón social" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="tipo_transporte"
                label="Tipo de Transporte"
                rules={[{ required: true, message: "Seleccione el tipo" }]}
              >
                <Select placeholder="Seleccione tipo">
                  {TIPOS_TRANSPORTE.map((tipo) => (
                    <Option key={tipo.value} value={tipo.value}>
                      {tipo.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Contacto</Divider>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="direccion" label="Dirección">
                <Input prefix={<HomeOutlined />} placeholder="Dirección" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="telefono"
                label="Teléfono"
                rules={[
                  {
                    pattern: /^[0-9+\s()-]+$/,
                    message: "Formato de teléfono inválido",
                  },
                ]}
              >
                <Input prefix={<PhoneOutlined />} placeholder="Teléfono" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="email"
                label="Correo Electrónico"
                rules={[
                  { type: "email", message: "Ingrese un correo válido" },
                ]}
              >
                <Input
                  prefix={<MailOutlined />}
                  placeholder="correo@ejemplo.com"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="zona_id" label="Zona">
                <Select
                  placeholder="Seleccione zona"
                  allowClear
                  showSearch
                  optionFilterProp="children"
                >
                  {zonas.map((zona) => (
                    <Option key={zona.id} value={zona.id}>
                      {zona.nombre}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Persona de Contacto Principal</Divider>
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item name="contacto_nombre" label="Nombre del Contacto">
                <Input
                  prefix={<UserOutlined />}
                  placeholder="Nombre del contacto"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="contacto_telefono"
                label="Teléfono del Contacto"
                rules={[
                  {
                    pattern: /^[0-9+\s()-]+$/,
                    message: "Formato de teléfono inválido",
                  },
                ]}
              >
                <Input
                  prefix={<PhoneOutlined />}
                  placeholder="Teléfono del contacto"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="contacto_cargo" label="Cargo">
                <Input placeholder="Cargo del contacto" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Persona de Contacto Adicional</Divider>
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item name="contacto2_nombre" label="Nombre del Contacto">
                <Input
                  prefix={<UserOutlined />}
                  placeholder="Nombre del contacto adicional"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="contacto2_telefono"
                label="Teléfono del Contacto"
                rules={[
                  {
                    pattern: /^[0-9+\s()-]+$/,
                    message: "Formato de teléfono inválido",
                  },
                ]}
              >
                <Input
                  prefix={<PhoneOutlined />}
                  placeholder="Teléfono del contacto"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="contacto2_cargo" label="Cargo">
                <Input placeholder="Cargo del contacto" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Observaciones</Divider>
          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item name="observaciones" label="Observaciones">
                <TextArea
                  rows={4}
                  placeholder="Observaciones adicionales sobre la transportadora"
                  maxLength={1000}
                  showCount
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">
            <CalendarOutlined /> Fechas de Contrato
          </Divider>
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item
                name="contrato_fecha_inicio"
                label="Fecha de Inicio"
                rules={[
                  { required: true, message: "La fecha de inicio es obligatoria" },
                ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD/MM/YYYY"
                  placeholder="Seleccione fecha de inicio"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="contrato_fecha_fin"
                label="Fecha de Fin"
                rules={[
                  {
                    required: !contratoIndefinido,
                    message: "La fecha de fin es obligatoria",
                  },
                ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD/MM/YYYY"
                  placeholder="Seleccione fecha de fin"
                  disabled={contratoIndefinido}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="contrato_indefinido"
                label="Contrato Indefinido"
                valuePropName="checked"
              >
                <Checkbox onChange={handleContratoIndefinidoChange}>
                  Sin fecha de finalización
                </Checkbox>
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Configuración</Divider>
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item
                name="estado"
                label="Estado"
                rules={[{ required: true, message: "Seleccione el estado" }]}
              >
                <Select placeholder="Estado">
                  <Option value="activo">Activo</Option>
                  <Option value="inactivo">Inactivo</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="es_mogotax_especial"
                label="Mogotax Especial"
                valuePropName="checked"
                tooltip="Marcar si es transportadora Mogotax Especial (aparece en flujo normal)"
              >
                <Switch checkedChildren="Sí" unCheckedChildren="No" />
              </Form.Item>
            </Col>
          </Row>

          {/* Botones de Acción */}
          <Form.Item style={{ marginTop: 24 }}>
            <Row justify="end">
              <Col>
                <Button
                  onClick={() => {
                    handleClose();
                    form.resetFields();
                  }}
                  style={{
                    backgroundColor: "#d32f2f",
                    color: "white",
                    borderColor: "#d32f2f",
                    borderRadius: "4px",
                    marginRight: "8px",
                  }}
                  icon={<CloseOutlined />}
                >
                  Cancelar
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={isSubmitting}
                  style={{
                    backgroundColor: "#0a2e5c",
                    color: "white",
                    borderColor: "#0a2e5c",
                    borderRadius: "4px",
                  }}
                  icon={<SaveOutlined />}
                >
                  {isEdit ? "Guardar Cambios" : "Guardar"}
                </Button>
              </Col>
            </Row>
          </Form.Item>
        </Spin>
      </Form>
    </Modal>
  );
};

TransportadoraForm.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  fetchTransportadoras: PropTypes.func.isRequired,
  initialData: PropTypes.object,
  zonas: PropTypes.array,
  isEdit: PropTypes.bool,
};

export default TransportadoraForm;
